import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8uei3k13ra8mig8v1g";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("提交成功，感谢您的反馈！");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name) {
      setStatus("error");
      setFormError("请填写您的姓名。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请填写有效的邮箱地址。");
      return;
    }
    if (message.length < 5) {
      setStatus("error");
      setFormError("反馈内容请至少填写 5 个字。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        setSuccessMsg("提交成功，感谢您的反馈，我们会尽快处理！");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "w-full h-11 rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors";

  return (
    <form
      id="contact-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7"
    >
      <input
        type="text"
        name="website_alt"
        className="form-honeypot-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-foreground-700 mb-2" htmlFor="fb-name">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的姓名"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm text-foreground-700 mb-2" htmlFor="fb-email">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="请输入您的邮箱"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm text-foreground-700 mb-2" htmlFor="fb-message">
          反馈内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入您的意见或建议（最多 500 字）"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
        ></textarea>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
        <p className="text-xs text-foreground-400">
          提交即表示您同意我们仅将信息用于问题处理与回复。
        </p>
      </div>

      {status === "success" && (
        <div className="mt-5 flex items-start gap-2.5 rounded-md bg-accent-100 border border-accent-200 px-4 py-3">
          <span className="w-5 h-5 flex items-center justify-center text-accent-700 shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </span>
          <p className="text-sm text-accent-900">{successMsg}</p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 flex items-start gap-2.5 rounded-md bg-primary-50 border border-primary-200 px-4 py-3">
          <span className="w-5 h-5 flex items-center justify-center text-primary-600 shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-lg"></i>
          </span>
          <p className="text-sm text-primary-800">{formError}</p>
        </div>
      )}
    </form>
  );
}